import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from '@/components/common/DataTable';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatNumber } from '@/utils/number';
import { donationCandidatesQueryOptions } from '../api/marketPromotionP2.api';
import { usePublishDonationBatch } from '../hooks/useMpP2Mutations';
import { defaultDateFrom, defaultDateTo } from '../utils/period';
import type { ColumnDef } from '@tanstack/react-table';
import type { DonationCandidate } from '../types/marketPromotionP2.types';

interface DonationPublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function keyOf(row: DonationCandidate): string {
  return `${row.userId}:${row.promoItemId ?? 0}`;
}

export function DonationPublishDialog({ open, onOpenChange }: DonationPublishDialogProps) {
  const [dateFrom, setDateFrom] = useState(defaultDateFrom());
  const [dateTo, setDateTo] = useState(defaultDateTo());
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');

  const { data, isLoading } = useQuery({
    ...donationCandidatesQueryOptions(dateFrom, dateTo),
    enabled: open,
  });

  const publish = usePublishDonationBatch(() => onOpenChange(false));

  const candidates = useMemo(() => data ?? [], [data]);

  function close(next: boolean) {
    if (!next) {
      setPicked(new Set());
      setAmount('');
      setComment('');
    }
    onOpenChange(next);
  }

  const chosen = useMemo(
    () => candidates.filter((row) => picked.has(keyOf(row))),
    [candidates, picked],
  );

  const items = new Set(chosen.map((row) => row.promoItemId));
  const oneItem = items.size === 1;
  const summed = chosen.reduce((sum, row) => sum + row.donationAmount, 0);

  const toggle = (row: DonationCandidate) =>
    setPicked((prev) => {
      const next = new Set(prev);
      const key = keyOf(row);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const columns: ColumnDef<DonationCandidate, unknown>[] = [
    {
      id: 'pick',
      header: '',
      cell: ({ row }) => (
        <Checkbox
          checked={picked.has(keyOf(row.original))}
          onCheckedChange={() => toggle(row.original)}
          aria-label={`Pick ${row.original.fullName ?? 'member'}`}
        />
      ),
    },
    {
      id: 'member',
      header: 'ZM Name',
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span className="font-medium">{row.original.fullName ?? '—'}</span>
          <span className="text-xs text-muted-foreground">
            {[row.original.hrEmpId, row.original.designation].filter(Boolean).join(' · ') || '—'}
          </span>
        </span>
      ),
    },
    { id: 'item', header: 'Donation Item', cell: ({ row }) => row.original.itemName ?? '—' },
    {
      id: 'count',
      header: 'Donations',
      cell: ({ row }) => <span className="tabular-nums">{row.original.donationCount}</span>,
    },
    {
      id: 'amount',
      header: 'Amount',
      cell: ({ row }) => (
        <span className="tabular-nums">
          {formatNumber(Math.round(row.original.donationAmount))}
        </span>
      ),
    },
  ];

  function submit() {
    const first = chosen[0];
    if (first?.promoItemId === undefined || first.promoItemId === null) return;

    publish.mutate({
      userIds: chosen.map((row) => row.userId),
      dateFrom,
      dateTo,
      promoItemId: first.promoItemId,
      amount: amount.trim() === '' ? summed : Number(amount),
      ...(comment.trim() === '' ? {} : { comment: comment.trim() }),
    });
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-3xl">
        <div className="min-w-0">
          <DialogHeader>
            <DialogTitle>Publish a Donation Batch</DialogTitle>
            <DialogDescription>
              The approved, unpublished cash donations entered in this window. A batch carries one
              donation item, so tick rows of a single item.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpd-from">Entry Date From</Label>
              <DatePickerInput id="mpd-from" value={dateFrom} onChange={setDateFrom} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpd-to">Entry Date To</Label>
              <DatePickerInput id="mpd-to" value={dateTo} onChange={setDateTo} />
            </div>
          </div>

          <div className="mt-4 max-h-[40vh] overflow-auto">
            <DataTable
              data={candidates}
              columns={columns}
              isLoading={isLoading}
              emptyMessage="No approved, unpublished donation in this window"
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpd-amount">Amount</Label>
              <Input
                id="mpd-amount"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder={formatNumber(Math.round(summed))}
                inputMode="numeric"
                autoComplete="off"
                className="tabular-nums"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpd-comment">Batch Name</Label>
              <Input
                id="mpd-comment"
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="What this batch is, e.g. August-2026"
                autoComplete="off"
              />
            </div>
          </div>

          {chosen.length > 0 && !oneItem ? (
            <p className="mt-3 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              Those rows span {items.size} donation items. A batch carries one, so tick rows of a
              single item.
            </p>
          ) : null}

          <DialogFooter className="mt-6">
            <p className="mr-auto text-sm text-muted-foreground">
              {chosen.length} picked ·{' '}
              <span className="tabular-nums">{formatNumber(Math.round(summed))}</span>
            </p>
            <Button type="button" variant="outline" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={publish.isPending || chosen.length === 0 || !oneItem}
            >
              {publish.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              Publish
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
